import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { startCheckout, downloadEbook, EBOOK_LOOKUP } from "@/lib/checkout";
import { ASSETS } from "@/lib/assets";
import { BET_TYPES, EBOOK_LEARN } from "@/lib/content";
import { api } from "@/lib/api";
import { BookOpen, Check, Download, Flame, Loader2 } from "lucide-react";
import ResponsibleBanner from "@/components/ResponsibleBanner";

export default function EbookPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState({ owned: false, available: false });

  useEffect(() => {
    api.get("/ebook/status").then((r) => setStatus(r.data)).catch(() => {});
  }, [user]);

  const buy = async () => {
    setBusy(true);
    await startCheckout(EBOOK_LOOKUP, user, navigate);
    setBusy(false);
  };

  return (
    <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-12 space-y-14">
      {/* Hero */}
      <section className="grid md:grid-cols-[320px_1fr] gap-10 items-center">
        <img src={ASSETS.ebookCover} alt="Sportsbook Smasher: Football Edition" className="w-full max-w-[300px] mx-auto rounded-2xl shadow-2xl" />
        <div>
          <div className="text-xs font-bold tracking-[0.2em] text-smasher-orange mb-2">THE SPORTSBOOK SMASHER EBOOK</div>
          <h1 className="font-heading font-black uppercase text-4xl sm:text-6xl text-gradient-gold leading-none mb-4">Football<br />Edition</h1>
          <p className="text-slate-300 leading-relaxed mb-6 max-w-xl">A no-nonsense guide that teaches sports betting concepts in an easy-to-understand way. Learn the core bet types, how odds and lines work, and a disciplined approach to reading the game before you ever place a wager.</p>
          {status.owned ? (
            <div className="space-y-2">
              <button onClick={downloadEbook} className="btn-gold px-8 py-3.5 rounded-xl font-heading font-black tracking-wide inline-flex items-center gap-2" data-testid="ebook-download-button">
                <Download className="w-5 h-5" /> DOWNLOAD YOUR EBOOK
              </button>
              <p className="text-xs text-emerald-400" data-testid="ebook-owned-note">{status.via_pro ? "Included free with your Pro Smasher membership." : "It's in your library — download it any time from My Area."}</p>
            </div>
          ) : (
            <div className="space-y-2">
              <button onClick={buy} disabled={busy} className="btn-gold px-8 py-3.5 rounded-xl font-heading font-black tracking-wide inline-flex items-center gap-2 disabled:opacity-60" data-testid="ebook-buy-button">
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <BookOpen className="w-5 h-5" />} GET THE EBOOK — $19.99
              </button>
              <p className="text-xs text-slate-500">Secure checkout. Instant download right after purchase.</p>
              <p className="text-xs text-smasher-gold font-bold" data-testid="ebook-pro-note">FREE with the $69/mo Pro Smasher plan — <Link to="/membership" className="underline hover:text-smasher-orange">see plans</Link></p>
            </div>
          )}
        </div>
      </section>

      {/* What you'll learn */}
      <section>
        <h2 className="font-heading font-black uppercase text-3xl text-white mb-6 flex items-center gap-2"><BookOpen className="w-7 h-7 text-smasher-gold" /> What You'll Learn</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {EBOOK_LEARN.map((l) => (
            <div key={l} className="panel p-4 flex items-start gap-3"><Check className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" /><span className="text-slate-200">{l}</span></div>
          ))}
        </div>
      </section>

      {/* Bet types */}
      <section>
        <h2 className="font-heading font-black uppercase text-3xl text-white mb-6">The Three Core Bet Types</h2>
        <div className="grid md:grid-cols-3 gap-5">
          {BET_TYPES.map((b) => (
            <div key={b.name} className="panel panel-hover p-6">
              <div className="font-mono-num text-smasher-gold/40 text-4xl font-bold">{b.n}</div>
              <h3 className="font-heading font-black text-xl text-smasher-gold mt-2">{b.name}</h3>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Bonus section */}
      <section className="panel p-6 sm:p-8 border-smasher-orange/30">
        <div className="text-xs font-bold tracking-[0.2em] text-smasher-orange mb-2 flex items-center gap-2"><Flame className="w-4 h-4" /> BONUS SECTION</div>
        <h2 className="font-heading font-black uppercase text-3xl text-white mb-4">My Live Betting Approach</h2>
        <p className="text-slate-300 leading-relaxed mb-4">This is the author's <span className="text-smasher-gold font-semibold">personal, educational approach</span> to live Over betting — an example of one way to think about in-game totals. It is not a guaranteed system.</p>
        <div className="bg-black/50 rounded-xl p-5 font-mono-num text-sm text-slate-300 space-y-1.5 mb-4">
          <div>At halftime, note the combined score.</div>
          <div className="text-white">Example: 20 – 10 at halftime = <span className="text-smasher-gold">30 combined points</span></div>
          <div>Double it: 30 × 2 = <span className="text-smasher-gold">60</span></div>
          <div>Adjust down ~5 for second-half slowdown: <span className="text-smasher-gold">55</span></div>
          <div>Compare that estimate to the sportsbook's live total.</div>
          <div>Only consider the wager if the live number creates what you view as a favorable spot.</div>
        </div>
        <p className="text-xs text-slate-500">This is an educational example of one thought process — one input among many. It is never a guaranteed winning formula. Betting involves risk and past results do not guarantee future results.</p>
      </section>

      <ResponsibleBanner />
    </div>
  );
}
